import React, { useEffect, useMemo, useRef, useState } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { ConfirmationModal, Table } from 'components/Ui'
import {
    updateApiRecord,
    testConnection,
    authorize,
    clearTokens,
    oneClickAuthorize,
    deleteApiRecord,
    syncAllXeroData,
    syncIncrementalXeroData,
} from 'services/XeroIntegrationService'
import { Success, Error } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { activeStatus } from 'components/helper'
import { Button } from 'components/Form'
import {
    HiOutlineCheckCircle,
    HiOutlineKey,
    HiOutlineRefresh,
    HiOutlineTrash,
    HiOutlinePencil,
    HiOutlineDownload,
} from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { useSearchParams, useNavigate } from 'react-router-dom'

injectReducer('xeroIntegration', reducer)

export default function XeroIntegration() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const authProcessedRef = useRef(false)
    const [syncLoading, setSyncLoading] = useState(false)
    const user = useSelector(state => state.auth.user)
    const { data, tableData, loading } = useSelector(
        state =>
            state.xeroIntegration?.data || {
                data: [],
                tableData: {
                    total: 0,
                    pageIndex: 1,
                    pageSize: 10,
                    query: '',
                    selectedTab: '',
                    sort: {
                        order: '',
                        key: '',
                    },
                },
                loading: false,
            }
    )

    // Get company ID for one-click authorization
    const companyId = useSelector(state => state.auth?.user?.company_id)
    const { company, customer } = useSelector(state => state.filterData)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        company,
        customer,
    ])

    // Handle OAuth callback - with protection against duplicate processing
    useEffect(() => {
        const code = searchParams.get('code')
        const configId = searchParams.get('config_id')
        const error = searchParams.get('error')

        // Only process if we have parameters and haven't processed them yet
        if ((code || configId || error) && !authProcessedRef.current) {
            // Mark as processed immediately to prevent duplicate processing
            authProcessedRef.current = true

            // Clear URL parameters to prevent reprocessing if page refreshes
            setSearchParams({}, { replace: true })

            // Handle any errors from the OAuth provider
            if (error) {
                console.log(`OAuth error received: ${error}`)
                Error(`OAuth error: ${error}`)
                return
            }

            // Process successful authorization
            if (code && configId) {
                console.log(`Processing authorization for config ID: ${configId}`)

                // Exchange authorization code for tokens - single API call
                authorize(configId, { code })
                    .then(response => {
                        if (response.data.success) {
                            console.log('Authorization successful')
                            Success('Authorization successful! Tokens have been saved.')
                            fetchData() // Refresh the table to show updated token status
                        } else {
                            console.log(`Authorization failed: ${response.data.message || 'Unknown error'}`)
                            Error(response.data.message || 'Authorization failed!')
                        }
                    })
                    .catch(error => {
                        console.error('Error during token exchange:', error)
                        // Extract the most specific error message possible
                        let errorMessage = 'Authorization failed!'
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message
                        } else if (error.response?.data?.error_description) {
                            errorMessage = error.response.data.error_description
                        } else if (error.message) {
                            errorMessage = error.message
                        }
                        Error(`Token exchange failed: ${errorMessage}`)
                    })
            }
        }

        // Reset the processed flag when URL params are empty (allows future auth attempts)
        if (!code && !configId && !error) {
            authProcessedRef.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    const fetchData = () => dispatch(getData({ ...tableData, company: company, customer: customer }))

    const handleStatus = (id, data) => {
        updateApiRecord(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch(error => {
                console.log('Error: ', error)
                Error('Failed to update status!')
            })
    }

    const handleTestConnection = id => {
        testConnection(id)
            .then(response => {
                console.log('Test connection response:', response.data)

                if (response.data.success) {
                    const orgInfo = response.data.organisation || 'Unknown Organization'
                    const tenantId = response.data.tenant_id || 'N/A'
                    const connectionsCount = response.data.connections_count || 1

                    Success(
                        `✅ Connection successful!\n` +
                            `Organization: ${orgInfo}\n` +
                            `Tenant ID: ${tenantId}\n` +
                            `Connections: ${connectionsCount}`
                    )
                    // Refresh the data to update the UI
                    fetchData()
                } else if (response.data.requires_auth && response.data.authorization_url) {
                    Success(`🔐 Authorization required. Opening authorization URL...`)
                    window.open(response.data.authorization_url, '_blank')
                } else {
                    // Enhanced error handling
                    let errorMessage = response.data.message || 'Connection test failed!'

                    // Add debug information if available
                    if (response.data.debug_info) {
                        const debugInfo = response.data.debug_info
                        const debugDetails = []

                        if (!debugInfo.has_access_token) {
                            debugDetails.push('❌ No access token')
                        } else if (debugInfo.token_expired) {
                            debugDetails.push('⏰ Token expired')
                        } else {
                            debugDetails.push('✅ Valid token')
                        }

                        if (!debugInfo.has_tenant_id) {
                            debugDetails.push('❌ No tenant ID')
                        }

                        if (debugDetails.length > 0) {
                            errorMessage += `\n\nStatus: ${debugDetails.join(', ')}`
                        }
                    }

                    // Add action required if available
                    if (response.data.action_required) {
                        errorMessage += `\n\n💡 ${response.data.action_required}`
                    }

                    Error(errorMessage)
                }
            })
            .catch(error => {
                console.log('Test connection error:', error)

                let errorMessage = 'Connection test failed!'

                // Try to extract more detailed error information
                if (error.response?.data) {
                    const errorData = error.response.data
                    errorMessage = errorData.message || errorMessage

                    if (errorData.error_type) {
                        errorMessage += `\n\nError Type: ${errorData.error_type}`
                    }

                    if (errorData.action_required) {
                        errorMessage += `\n\n💡 ${errorData.action_required}`
                    }

                    // Add debug info if available
                    if (errorData.debug_info) {
                        errorMessage += '\n\nFor more details, check the browser console.'
                        console.error('Detailed error info:', errorData.debug_info)
                    }
                } else if (error.message) {
                    errorMessage += `\n\nError: ${error.message}`
                }

                Error(errorMessage)
            })
    }

    const handleReauthorize = id => {
        // First, clear the existing tokens by forcing a test connection that will trigger OAuth
        testConnection(id)
            .then(response => {
                console.log('Reauthorization response:', response.data)

                if (response.data.requires_auth && response.data.authorization_url) {
                    Success(`🔐 Opening authorization URL for re-authorization...`)
                    window.open(response.data.authorization_url, '_blank')
                } else if (response.data.authorization_url) {
                    // Even if success is false, if we have auth URL, use it
                    Success(`🔐 Opening authorization URL for re-authorization...`)
                    window.open(response.data.authorization_url, '_blank')
                } else {
                    // Try to manually clear tokens and try again
                    Error('Unable to generate authorization URL. Please try clearing tokens first.')
                }
            })
            .catch(error => {
                console.log('Reauthorization error:', error)
                Error('Failed to initiate re-authorization!')
            })
    }

    const handleClearTokens = id => {
        // Clear tokens using the dedicated endpoint
        clearTokens(id)
            .then(response => {
                if (response.data.success) {
                    Success('✅ Tokens cleared successfully! You can now re-authorize.')
                    fetchData()
                } else {
                    Error(response.data.message || 'Failed to clear tokens!')
                }
            })
            .catch(error => {
                console.log('Error clearing tokens:', error)
                const errorMessage = error.response?.data?.message || 'Failed to clear tokens!'
                Error(errorMessage)
            })
    }

    // Handle delete integration
    const handleDelete = id => {
        deleteApiRecord(id)
            .then(response => {
                Success('Xero integration deleted successfully!')
                fetchData()
            })
            .catch(error => {
                console.log('Error deleting integration:', error)
                const errorMessage = error.response?.data?.message || 'Failed to delete integration!'
                Error(errorMessage)
            })
    }

    // Handle edit integration
    const handleEdit = id => {
        navigate(`/company-management/xero-integration/${id}/edit`)
    }

    // Simple sync function for the top button - no company ID needed
    const handleSync = () => {
        setSyncLoading(true)
        syncIncrementalXeroData()
            .then(response => {
                Success('Incremental sync initiated. This will sync data modified in the last 2 days.')
            })
            .catch(error => {
                console.error('Error syncing Xero data:', error)
                const errorMessage = error.response?.data?.message || 'Failed to sync Xero data'
                Error(errorMessage)
            })
            .finally(() => {
                setSyncLoading(false)
            })
    }

    // Handle sync all Xero data for a company
    const handleSyncAllData = company => {
        if (!company || !company.id) {
            Error('Company information not found. Cannot sync data.')
            return
        }

        syncAllXeroData(company.id)
            .then(response => {
                if (response.data.success) {
                    Success(`Full sync initiated for ${company.name}. This may take a few minutes to complete.`)
                    // No need to refresh the table as this doesn't change the configuration status
                } else {
                    Error(response.data.message || 'Failed to sync Xero data')
                }
            })
            .catch(error => {
                console.error('Error syncing Xero data:', error)
                const errorMessage = error.response?.data?.message || 'Failed to sync Xero data'
                Error(errorMessage)
            })
    }

    // Handle incremental sync for a company
    const handleSyncIncrementalData = company => {
        if (!company || !company.id) {
            Error('Company information not found. Cannot sync data.')
            return
        }

        syncIncrementalXeroData(company.id)
            .then(response => {
                if (response.data.success) {
                    Success(`Incremental sync initiated for ${company.name}. This will sync data modified in the last 2 days.`)
                    // No need to refresh the table as this doesn't change the configuration status
                } else {
                    Error(response.data.message || 'Failed to sync Xero data')
                }
            })
            .catch(error => {
                console.error('Error syncing Xero data:', error)
                const errorMessage = error.response?.data?.message || 'Failed to sync Xero data'
                Error(errorMessage)
            })
    }

    // New one-click authorization function
    const handleOneClickAuthorize = () => {
        if (!companyId) {
            Error('Company ID not found. Please ensure you are logged in properly.')
            return
        }

        oneClickAuthorize(companyId)
            .then(response => {
                if (response.data.success && response.data.authorization_url) {
                    Success('🔐 Opening Xero authorization page...')
                    window.open(response.data.authorization_url, '_blank')
                } else {
                    Error(response.data.message || 'Failed to initiate authorization!')
                }
            })
            .catch(error => {
                console.log('One-click authorization error:', error)
                const errorMessage = error.response?.data?.message || 'Failed to initiate authorization!'
                Error(errorMessage)
            })
    }

    // Handle refresh token
    const handleRefreshToken = id => {
        // Call the refresh token endpoint
        fetch(`/api/v1/xero/configurations/${id}/refresh_token/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    Success('✅ Token refreshed successfully!')
                    fetchData() // Refresh the table to show updated token status
                } else {
                    Error(data.message || 'Failed to refresh token!')
                }
            })
            .catch(error => {
                console.error('Error refreshing token:', error)
                Error('Failed to refresh token!')
            })
    }

    // Function to check if reauthorization is needed
    const needsReauthorization = row => {
        // If we have tokens but they're not working (this would be set by test connection results)
        // For now, we'll show it if they have tokens but might need reauth
        return row.has_access_token && row.tenant_id && !row.is_token_expired
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row?.company?.name || row?.company?.code || 'N/A',
            },
            {
                field: 'customer',
                header: 'Customer',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row?.customer?.customer_code || 'N/A',
            },
            {
                field: 'client_id',
                header: 'Client ID',
                className: 'min-w-[180px]',
                hide: false,
                renderCell: row => <span className="font-mono text-sm">{row.client_id?.substring(0, 20)}...</span>,
            },
            {
                field: 'tenant_id',
                header: 'Tenant ID',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => {
                    const value = row.tenant_id
                    return value ? (
                        <span className="font-mono text-sm">{value.substring(0, 15)}...</span>
                    ) : (
                        <span className="text-gray-400">Not authorized</span>
                    )
                },
            },
            {
                field: 'is_test_mode',
                header: 'Test Mode',
                className: 'min-w-[100px]',
                hide: false,
                renderCell: row => (
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            row.is_test_mode
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}
                    >
                        {row.is_test_mode ? 'Test' : 'Live'}
                    </span>
                ),
            },
            {
                header: 'Status',
                field: 'is_active',
                hide: false,
                renderCell: row => {
                    return checkPermission('xero-integration', 'status') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, { is_active: !row.is_active })
                            }}
                        >
                            {activeStatus(row.is_active)}
                        </ConfirmationModal>
                    ) : (
                        activeStatus(row.is_active)
                    )
                },
            },
            {
                field: 'is_token_expired',
                header: 'Token Status',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => {
                    const isExpired = row.is_token_expired
                    const hasToken = row.has_access_token

                    if (!hasToken) {
                        return <span className="text-gray-400">No token</span>
                    }

                    return (
                        <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                isExpired
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}
                        >
                            {isExpired ? 'Expired' : 'Valid'}
                        </span>
                    )
                },
            },
            {
                header: 'Actions',
                field: 'actions',
                className: 'min-w-[180px]',
                hide: false,
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-1">
                            <Tooltip title="Test Connection">
                                <Button
                                    size="sm"
                                    variant="twoTone"
                                    color="blue-600"
                                    onClick={() => handleTestConnection(row.id)}
                                    className="!px-2 !py-1"
                                >
                                    <HiOutlineCheckCircle size={16} />
                                </Button>
                            </Tooltip>

                            {/* Initial authorization - show when no token or expired */}
                            {(!row.has_access_token || row.is_token_expired) && (
                                <Tooltip title="Authorize">
                                    <Button
                                        size="sm"
                                        variant="twoTone"
                                        color="green-600"
                                        onClick={() => handleTestConnection(row.id)}
                                        className="!px-2 !py-1"
                                    >
                                        <HiOutlineKey size={16} />
                                    </Button>
                                </Tooltip>
                            )}

                            {/* Clear tokens - show when we have tokens */}
                            {row.has_access_token && (
                                <Tooltip title="Clear Tokens">
                                    <ConfirmationModal
                                        onConfirm={() => handleClearTokens(row.id)}
                                        confirmText="Clear Tokens"
                                        title="Clear Xero Tokens"
                                        description="This will remove all authentication tokens. You'll need to re-authorize afterwards."
                                    >
                                        <Button size="sm" variant="twoTone" color="red-600" className="!px-2 !py-1">
                                            <HiOutlineRefresh size={16} />
                                        </Button>
                                    </ConfirmationModal>
                                </Tooltip>
                            )}

                            {/* Edit integration */}
                            {checkPermission('xero-integration', 'update') && (
                                <Tooltip title="Edit Integration">
                                    <Button
                                        size="sm"
                                        variant="twoTone"
                                        color="indigo-600"
                                        onClick={() => handleEdit(row.id)}
                                        className="!px-2 !py-1"
                                    >
                                        <HiOutlinePencil size={16} />
                                    </Button>
                                </Tooltip>
                            )}

                            {/* Full Sync with confirmation - only for super admins */}
                            {row.has_access_token && !row.is_token_expired && user?.super_user && (
                                <Tooltip title="Full Sync (All Data)">
                                    <ConfirmationModal
                                        onConfirm={() => handleSyncAllData(row.company)}
                                        title="Full Xero Data Sync"
                                        message={`Are you sure you want to perform a full sync for ${row.company?.name}? This will sync all data and may take several minutes to complete.`}
                                    >
                                        <Button size="sm" variant="twoTone" color="purple-600" className="!px-2 !py-1">
                                            <HiOutlineDownload size={16} />
                                        </Button>
                                    </ConfirmationModal>
                                </Tooltip>
                            )}

                            {/* Delete integration */}
                            {checkPermission('xero-integration', 'delete') && (
                                <Tooltip title="Delete Integration">
                                    <ConfirmationModal
                                        onConfirm={() => handleDelete(row.id)}
                                        confirmText="Delete Integration"
                                        title="Delete Xero Integration"
                                        description="Are you sure you want to delete this Xero integration? This action cannot be undone."
                                    >
                                        <Button size="sm" variant="twoTone" color="red-600" className="!px-2 !py-1">
                                            <HiOutlineTrash size={16} />
                                        </Button>
                                    </ConfirmationModal>
                                </Tooltip>
                            )}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            company,
            customer,
        ]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Xero Integration</h2>

                <div className="flex gap-2">
                    {/* Incremental Sync button */}
                    {/* <Tooltip title="Incremental sync (last 2 days)">
                        <Button
                            variant="twoTone"
                            color="blue-600"
                            onClick={handleSync}
                            disabled={syncLoading}
                            className="flex items-center gap-2"
                        >
                            <HiOutlineRefresh className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                            {syncLoading ? 'Syncing...' : 'Incremental Sync'}
                        </Button>
                    </Tooltip> */}

                    {/* Full Sync button with confirmation - only for super admins */}
                    {user?.super_user && (
                        <Tooltip title="Full sync (all data)">
                            <ConfirmationModal
                                onConfirm={() => {
                                    setSyncLoading(true)
                                    syncAllXeroData()
                                        .then(response => {
                                            Success(
                                                'Full sync initiated for all companies. This may take several minutes to complete.'
                                            )
                                        })
                                        .catch(error => {
                                            console.error('Error syncing Xero data:', error)
                                            const errorMessage = error.response?.data?.message || 'Failed to sync Xero data'
                                            Error(errorMessage)
                                        })
                                        .finally(() => {
                                            setSyncLoading(false)
                                        })
                                }}
                                title="Full Xero Data Sync"
                                message="Are you sure you want to perform a full sync for all companies? This will sync all data and may take several minutes to complete."
                            >
                                <Button
                                    variant="twoTone"
                                    color="purple-600"
                                    disabled={syncLoading}
                                    className="flex items-center gap-2"
                                >
                                    <HiOutlineDownload className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                                    {syncLoading ? 'Syncing...' : 'Full Sync'}
                                </Button>
                            </ConfirmationModal>
                        </Tooltip>
                    )}

                    {/* One-click authorization button */}
                    {/* {companyId && checkPermission('xero-integration', 'create') && (
                        <Button
                            size="sm"
                            variant="solid"
                            color="blue-600"
                            onClick={handleOneClickAuthorize}
                            className="flex items-center gap-1"
                        >
                            <HiOutlineKey size={16} /> One-Click Xero Authorization
                        </Button>
                    )} */}
                </div>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={checkPermission('xero-integration', 'create') ? '/company-management/xero-integration/create' : ''}
                pagingData={tableData}
                onChange={e => dispatch(setTableData(e))}
            />
        </>
    )
}
